import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, FileSpreadsheet, Download, RefreshCw, 
  Users, Calendar, Target, Trophy, Brain, TrendingUp, FileText, FileDown
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';

const REPORT_ICONS = {
  'program_summary': Users,
  'weekly_checkin_summary': Calendar,
  'participant_progress': TrendingUp,
  'engagement': Target,
  'skills_assessment': Brain,
  'goals': Target,
  'wins': Trophy
};

const ReportsPage = ({ user }) => {
  const navigate = useNavigate();
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(null);

  useEffect(() => {
    fetchReports();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchReports = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/reports/available`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.ok) {
        const data = await res.json();
        setReports(data.reports || []);
      }
    } catch (error) {
      console.error('Failed to fetch reports:', error);
      toast.error('Failed to load reports');
    } finally {
      setLoading(false);
    }
  };

  const downloadReport = async (reportId, reportName, format = 'csv') => {
    setDownloading(reportId + format);
    try {
      const token = localStorage.getItem('token');
      
      let url;
      let filename;
      
      if (format === 'pdf') {
        // Map to PDF endpoints
        const pdfEndpoints = {
          'program_overview': 'program-overview',
          'funder_summary': 'funder-summary'
        };
        const endpoint = pdfEndpoints[reportId] || reportId;
        url = `${process.env.REACT_APP_BACKEND_URL}/api/reports/pdf/${endpoint}`;
        filename = `${reportId}_${new Date().toISOString().split('T')[0]}.pdf`;
      } else {
        // CSV endpoints
        const endpointMap = {
          'program_summary': 'program-summary',
          'weekly_checkin_summary': 'weekly-checkin-summary',
          'participant_progress': 'participant-progress',
          'engagement': 'engagement',
          'skills_assessment': 'skills-assessment',
          'goals': 'goals',
          'wins': 'wins'
        };
        const endpoint = endpointMap[reportId] || reportId;
        url = `${process.env.REACT_APP_BACKEND_URL}/api/reports/${endpoint}`;
        filename = `${reportId}_${new Date().toISOString().split('T')[0]}.csv`;
      }
      
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      
      if (res.ok) {
        const blob = await res.blob();
        const downloadUrl = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = downloadUrl;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(downloadUrl);
        document.body.removeChild(a);
        toast.success(`${reportName} downloaded!`);
      } else {
        toast.error('Failed to download report');
      }
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Failed to download report');
    } finally {
      setDownloading(null);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <RefreshCw className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen pb-8" style={{ backgroundColor: '#F5F3EF' }}>
      {/* Header */}
      <div className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center gap-3">
            <button 
              onClick={() => navigate(-1)}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              data-testid="back-button"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <h1 className="text-xl font-bold text-gray-900">Data Reports</h1>
              <p className="text-sm text-gray-500">Export program data as CSV or PDF</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-6">
        <Tabs defaultValue="pdf" className="space-y-6">
          <TabsList className="bg-white rounded-xl p-1 shadow-sm w-full">
            <TabsTrigger value="pdf" className="flex-1 rounded-lg gap-2" data-testid="pdf-tab">
              <FileDown className="w-4 h-4" /> PDF Reports
            </TabsTrigger>
            <TabsTrigger value="csv" className="flex-1 rounded-lg gap-2" data-testid="csv-tab">
              <FileSpreadsheet className="w-4 h-4" /> CSV Data Export
            </TabsTrigger>
          </TabsList>

          {/* PDF Reports */}
          <TabsContent value="pdf">
            <div className="bg-gradient-to-r from-orange-50 to-amber-50 border border-orange-200 rounded-xl p-4 mb-6">
              <div className="flex items-start gap-3">
                <FileDown className="w-5 h-5 text-orange-600 mt-0.5" />
                <div>
                  <h3 className="font-semibold text-orange-900">Professional PDF Reports</h3>
                  <p className="text-sm text-orange-700 mt-1">
                    Branded, presentation-ready reports with charts and statistics. Perfect for funders and stakeholders.
                  </p>
                </div>
              </div>
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              {[
                { 
                  id: 'program_overview', 
                  name: 'Program Overview Report', 
                  description: 'Complete program snapshot with enrollment, engagement metrics, top performers, and worksite distribution.',
                  icon: Users
                },
                { 
                  id: 'funder_summary', 
                  name: 'Funder Impact Summary', 
                  description: 'Executive summary designed for funders and stakeholders with key impact metrics and engagement analysis.',
                  icon: TrendingUp
                },
              ].map(report => {
                const Icon = report.icon;
                return (
                  <Card key={report.id} className="p-5 hover:shadow-md transition-shadow bg-white">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center flex-shrink-0">
                        <Icon className="w-6 h-6 text-orange-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-gray-900">{report.name}</h3>
                        <p className="text-sm text-gray-500 mt-1">{report.description}</p>
                        <Button
                          variant="default"
                          size="sm"
                          className="mt-3 gap-2 bg-orange-500 hover:bg-orange-600 text-white rounded-lg"
                          onClick={() => downloadReport(report.id, report.name, 'pdf')}
                          disabled={downloading === report.id + 'pdf'}
                          data-testid={`download-pdf-${report.id}`}
                        >
                          {downloading === report.id + 'pdf' ? (
                            <><RefreshCw className="w-4 h-4 animate-spin" /> Generating...</>
                          ) : (
                            <><FileDown className="w-4 h-4" /> Download PDF</>
                          )}
                        </Button>
                      </div>
                    </div>
                  </Card>
                );
              })}
            </div>
          </TabsContent>

          {/* CSV Reports */}
          <TabsContent value="csv">
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-6">
              <div className="flex items-start gap-3">
                <FileSpreadsheet className="w-5 h-5 text-blue-600 mt-0.5" />
                <div>
                  <h3 className="font-semibold text-blue-900">CSV Format</h3>
                  <p className="text-sm text-blue-700 mt-1">
                    Raw data exports that can be opened in Excel, Google Sheets, or any spreadsheet application.
                  </p>
                </div>
              </div>
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              {reports.map((report) => {
                const Icon = REPORT_ICONS[report.id] || FileText;
                return (
                  <Card key={report.id} className="p-5 hover:shadow-md transition-shadow bg-white">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 bg-blue-50 rounded-xl flex items-center justify-center flex-shrink-0">
                        <Icon className="w-6 h-6 text-blue-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-gray-900">{report.name}</h3>
                        <p className="text-sm text-gray-500 mt-1 line-clamp-2">{report.description}</p>
                        <Button
                          variant="outline"
                          size="sm"
                          className="mt-3 gap-2"
                          onClick={() => downloadReport(report.id, report.name, 'csv')}
                          disabled={downloading === report.id + 'csv'}
                          data-testid={`download-csv-${report.id}`}
                        >
                          {downloading === report.id + 'csv' ? (
                            <><RefreshCw className="w-4 h-4 animate-spin" /> Downloading...</>
                          ) : (
                            <><Download className="w-4 h-4" /> Download CSV</>
                          )}
                        </Button>
                      </div>
                    </div>
                  </Card>
                );
              })}
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default ReportsPage;
